import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * AppEntry - PWA entry point with branded loading experience
 * 
 * This page handles the PWA start_url redirect logic:
 * - If user is logged in: redirects to their dashboard
 * - If user is not logged in: redirects to signin
 * 
 * Features an animated splash screen that smoothly transitions
 * to the destination page.
 */
function AppEntry() {
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Start transition animation
      setIsTransitioning(true);
      
      // Small delay for smooth transition
      setTimeout(() => {
        if (user) {
          // User is signed in, redirect to their dashboard
          navigate(`/${user.uid}/dashboard`, { replace: true });
        } else {
          // User is not signed in, redirect to signin
          navigate('/signin', { replace: true });
        }
      }, 300);
    });

    return () => unsubscribe();
  }, [navigate]);

  return (
    <div className={`app-entry-splash ${isTransitioning ? 'transitioning' : ''}`}>
      {/* Background glow effects */}
      <div className="app-entry-glow app-entry-glow-1" />
      <div className="app-entry-glow app-entry-glow-2" />
      
      {/* Logo with pulse animation */}
      <div className="app-entry-logo-container">
        <div className="app-entry-logo-glow" />
        <img 
          src="/apple-touch-icon.png" 
          alt="Begin Strength" 
          className="app-entry-logo"
        />
      </div>
      
      {/* Animated loading bar */}
      <div className="app-entry-loading-bar">
        <div className="app-entry-loading-progress" />
      </div>
      
      {/* Inline styles for this component */}
      <style>{`
        .app-entry-splash {
          min-height: 100vh;
          min-height: 100dvh;
          background: #1D1D1D;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          transition: opacity 0.3s ease-out, transform 0.3s ease-out;
        }
        
        .app-entry-splash.transitioning {
          opacity: 0;
          transform: scale(1.02);
        }
        
        /* Background glow effects */
        .app-entry-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.4;
          pointer-events: none;
        }
        
        .app-entry-glow-1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%);
          top: 20%;
          left: 30%;
          animation: app-entry-float 6s ease-in-out infinite;
        }
        
        .app-entry-glow-2 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(255, 60, 0, 0.2) 0%, transparent 70%);
          bottom: 20%;
          right: 20%;
          animation: app-entry-float 8s ease-in-out infinite reverse;
        }
        
        @keyframes app-entry-float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-20px, -30px) scale(1.1);
          }
        }
        
        /* Logo container */
        .app-entry-logo-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        
        /* Pulsing glow behind logo */
        .app-entry-logo-glow {
          position: absolute;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 0, 0, 0.35) 0%, rgba(255, 0, 0, 0) 70%);
          border-radius: 50%;
          animation: app-entry-pulse 2s ease-in-out infinite;
        }
        
        /* Logo with breathing animation */
        .app-entry-logo {
          width: 96px;
          height: 96px;
          animation: app-entry-breathe 2s ease-in-out infinite;
          position: relative;
          z-index: 1;
        }
        
        @keyframes app-entry-breathe {
          0%, 100% {
            transform: scale(1);
            filter: brightness(1) drop-shadow(0 0 20px rgba(255, 0, 0, 0.3));
          }
          50% {
            transform: scale(1.05);
            filter: brightness(1.1) drop-shadow(0 0 30px rgba(255, 0, 0, 0.5));
          }
        }
        
        @keyframes app-entry-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.4);
            opacity: 1;
          }
        }
        
        /* Loading bar */
        .app-entry-loading-bar {
          width: 140px;
          height: 3px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          margin-top: 40px;
          overflow: hidden;
          position: relative;
          z-index: 1;
        }
        
        .app-entry-loading-progress {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 50%;
          background: linear-gradient(90deg, transparent, #FF0000, transparent);
          border-radius: 2px;
          animation: app-entry-loading 1s ease-in-out infinite;
        }
        
        @keyframes app-entry-loading {
          0% {
            left: -50%;
          }
          100% {
            left: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default AppEntry;
